// libraryViewModel.js — Task 9: pure derivations over the checked-in agent library registry.
//
// The registry (agentLibraryRegistry.json) is the SINGLE source of truth for the curated agent
// catalog. It is read by BOTH webpack (this module's synchronous `import`, so the dock/slider
// consume it without a fetch and can never go blank on a network failure) AND Rails
// (AgentLibrary::Registry reads the same file). Every function here is pure and node-testable —
// no React, no DOM, no fetch.
//
// JSON-MODULE GOTCHA (verified 2026-07-17, Node 22 ESM): the import MUST use the import-attribute
// form `with { type: "json" }` — a bare JSON import throws ERR_IMPORT_ATTRIBUTE_MISSING under
// `node --test`. Confirmed parseable by this repo's Babel (@babel/core 7.26) and webpack 5.98.
import registry from "./agentLibraryRegistry.json" with { type: "json" }

// ── Channel badge palette ──────────────────────────────────────────────────────────────────
// Canonical channel → { label, color } lookup, extended from the original dock palette with the
// three library-only channel keys (reporting/ticketing/ops). dockSuggestionsViewModel re-exports
// this so there is ONE palette across dock, slider, and the library page.
export const CHANNELS = Object.freeze({
  meta: { label: "Meta Ads", color: "#6A3AFB" },
  google: { label: "Google Ads", color: "#4285F4" },
  social: { label: "Social", color: "#34C759" },
  email: { label: "Email", color: "#FF7322" },
  press: { label: "Press", color: "#FF9500" },
  calendars: { label: "Calendars", color: "#A500C6" },
  reporting: { label: "Reporting", color: "#0EA5E9" },
  ticketing: { label: "Ticketing", color: "#F43F5E" },
  ops: { label: "Ops", color: "#0D9488" },
})

export function channelInfo(ch) {
  return CHANNELS[ch] || { label: ch || "", color: "#94A3B8" }
}

// ── Registry validation + access ─────────────────────────────────────────────────────────────
// Validate the structural invariants a well-formed registry MUST hold and return it. Throws on a
// malformed entry so a bad edit fails loudly in dev/test (the checked-in file is the review
// surface; a Rails spec independently proves every ready job's trigger_spec normalizes).
//   - ids are unique
//   - every task entry has a non-empty prompt
//   - every job entry has job_seed.trigger_spec + job_seed.trigger_label
let _validated = null
export function getRegistry() {
  if (_validated) return _validated
  const reg = registry || {}
  const entries = Array.isArray(reg.entries) ? reg.entries : []

  const seen = new Set()
  for (const e of entries) {
    if (!e || typeof e.id !== "string" || !e.id.trim()) {
      throw new Error(`agentLibraryRegistry: entry missing a valid id (${JSON.stringify(e && e.id)})`)
    }
    if (seen.has(e.id)) throw new Error(`agentLibraryRegistry: duplicate id "${e.id}"`)
    seen.add(e.id)

    if (e.kind === "task") {
      if (typeof e.prompt !== "string" || !e.prompt.trim()) {
        throw new Error(`agentLibraryRegistry: task "${e.id}" is missing a prompt`)
      }
    } else if (e.kind === "job") {
      const seed = e.job_seed || {}
      if (!seed.trigger_spec || typeof seed.trigger_spec !== "object") {
        throw new Error(`agentLibraryRegistry: job "${e.id}" is missing job_seed.trigger_spec`)
      }
      if (typeof seed.trigger_label !== "string" || !seed.trigger_label.trim()) {
        throw new Error(`agentLibraryRegistry: job "${e.id}" is missing job_seed.trigger_label`)
      }
    } else {
      throw new Error(`agentLibraryRegistry: entry "${e.id}" has an invalid kind (${JSON.stringify(e.kind)})`)
    }
  }

  _validated = {
    version: reg.version,
    use_cases: Array.isArray(reg.use_cases) ? reg.use_cases.slice() : [],
    categories: Array.isArray(reg.categories) ? reg.categories.slice() : [],
    entries,
  }
  return _validated
}

// ── Library page sections ──────────────────────────────────────────────────────────────────
// A Featured section (featured_library entries, in registry order) first, then one section per
// category in the registry's `categories` order (each holding every entry in that category,
// featured or not). Empty categories are omitted.
export function buildLibrarySections(reg) {
  const r = reg || getRegistry()
  const entries = r.entries || []
  const sections = []

  const featured = entries.filter((e) => e.featured_library)
  if (featured.length) sections.push({ key: "featured", category: "Featured", entries: featured })

  for (const category of r.categories || []) {
    const inCat = entries.filter((e) => e.category === category)
    if (inCat.length) sections.push({ key: category, category, entries: inCat })
  }
  return sections
}

// ── Search / use-case filter ───────────────────────────────────────────────────────────────
// Case-insensitive substring match over title/pitch/output/category/use_case, plus an exact
// use_case chip filter ("All" = null/undefined/"" → no use_case narrowing). Pure; returns a NEW
// array.
export function filterEntries(entries, { query = "", useCase = null } = {}) {
  const q = String(query || "").trim().toLowerCase()
  const uc = useCase && useCase !== "All" ? useCase : null
  return (entries || []).filter((e) => {
    if (uc && e.use_case !== uc) return false
    if (!q) return true
    const hay = [e.title, e.pitch, e.output, e.category, e.use_case]
      .map((x) => String(x || "").toLowerCase())
      .join(" ")
    return hay.includes(q)
  })
}

// ── Dock items (featured_dock → the dock's existing flat item shape) ─────────────────────────
// { id, kind, ch, chLabel, chColor, title, desc, msg }. Tasks first, then jobs (tab filters by
// kind — the dock renders tasks/jobs on separate tabs). For a job, `msg` is the deterministic
// chat-creation template PLUS a verbatim trigger_spec JSON line — this hands the agent the exact
// raw trigger to use, mitigating the C1 blind-guess on the in-chat job-creation path.
function dockMsgForJob(entry) {
  const seed = entry.job_seed || {}
  return `Set up an agent job: "${entry.title}". Trigger: ${seed.trigger_label}. Action: ${seed.action}. ` +
    `Use exactly this trigger_spec JSON when creating the job: ${JSON.stringify(seed.trigger_spec)}`
}

function toDockItem(entry) {
  const info = channelInfo(entry.channel)
  return {
    id: entry.id,
    kind: entry.kind,
    ch: entry.channel,
    chLabel: info.label,
    chColor: info.color,
    title: entry.title,
    desc: entry.pitch,
    msg: entry.kind === "job" ? dockMsgForJob(entry) : entry.prompt,
  }
}

export function buildDockItems(reg) {
  const r = reg || getRegistry()
  const featured = (r.entries || []).filter((e) => e.featured_dock && e.status === "ready")
  const tasks = featured.filter((e) => e.kind === "task").map(toDockItem)
  const jobs = featured.filter((e) => e.kind === "job").map(toDockItem)
  return [...tasks, ...jobs]
}

// ── Slider presets (featured_slider job entries → the Agent Jobs carousel preset shape) ──────
// The preset object is both the carousel display model AND the builder seed. `librarySeed` is the
// launch descriptor's jobSeed (rawTrigger passthrough + paramInputs) that openSuggestion feeds to
// NewAgentForm's library-trigger mode.
export function buildSliderPresets(reg) {
  const r = reg || getRegistry()
  return (r.entries || [])
    .filter((e) => e.featured_slider && e.kind === "job" && e.status === "ready")
    .map((e) => {
      const seed = e.job_seed || {}
      const desc = launchDescriptor(e)
      return {
        id: e.id,
        name: seed.name || e.title,
        icon: seed.icon || "sparkle",
        kind: seed.kind,
        trigger: seed.trigger_label,
        short: e.pitch,
        action: seed.action,
        scope: (seed.scope || []).slice(),
        note: e.note || null,
        kindIcon: KIND_ICON[seed.kind] || "clock",
        scopeLabels: (seed.scope || []).map((c) => channelInfo(c).label),
        librarySeed: desc.jobSeed,
      }
    })
}

// Kept local (mirrors triggeredListViewModel.KIND_ICON) so this module has no cyclic import.
const KIND_ICON = Object.freeze({ schedule: "clock", event: "zap", condition: "sliders", chat: "send" })

// ── Launch descriptor (what a library CTA hands the shell) ───────────────────────────────────
// Item #6 unification (charter: identical UI, ALWAYS — no card-level fork): EVERY entry now
// launches the same full NewAgentForm.
// task → { kind:'job', jobSeed:{ kind:'chat', action: prompt, ... } } — a quick task becomes a
//         real agent_jobs row of the new chat-invoked kind; its prompt IS the job's action text
//         (what list_agent_jobs hands the agent for inline execution). No rawTrigger (chat has no
//         machine trigger), so the form opens in its normal full-grammar mode with the chat tile
//         pre-selected.
// job  → { kind:'job', jobSeed:{ name, icon, action, kind, rawTrigger, triggerLabel, scope,
//          paramInputs, modelId, effort } } — rawTrigger is the trigger_spec VERBATIM, so the
//          builder never asks the form grammar to re-derive the trigger (bypasses the
//          monthly/vocabulary ceilings). A3: modelId/effort surface job_seed.model_id/job_seed.effort
//          verbatim (canonical full ids, e.g. "claude-sonnet-4-6") so NewAgentForm can pre-populate
//          its Model/Effort override control with the template's registry default.

// ── Setup prefill (A12) — presentation-only over the server-computed gap result ────────────────
// buildSetupPrefill takes ONLY the verbatim computeSetupGaps output — { known, gaps } — fetched by
// AgentJobs::SetupGaps -> POST /setup-gaps (vas-foundation-merge). It renders that result: every
// known qid becomes a prefilled value; every gap qid is simply absent from the returned map (the
// caller renders those fields empty). It takes NO `required`/registry argument and performs NO
// `required \ known` set subtraction of its own — the server is the ONLY place a gap set is
// computed. Do not add a second derivation here; that is the exact defect this area closes (AC8a's
// single-source structural teeth grep this function's body for an independent `required` set).
export function buildSetupPrefill(setupGaps) {
  const known = (setupGaps && setupGaps.known) || {}
  const prefill = {}
  for (const qid of Object.keys(known)) {
    prefill[qid] = known[qid].value
  }
  return prefill
}

// Item #6 gate-awareness (repair round, 2026-07-28 — Opus-grade must-fix BC-8): EVERY card now
// launches the same agent-job builder (launchDescriptor emits ONLY job descriptors — a quick task
// seeds the form with kind:'chat'), so the agent_triggers gate blocks EVERY card, not just
// kind:'job' ones. Keying the block off entry.kind let a task card open a builder whose create the
// server 403s. The gate keys off the LAUNCH TARGET; coming_soon still wins (never launchable).
export function cardCtaState(entry, gateOn) {
  const e = entry || {}
  if (e.status === "coming_soon") return "coming_soon"
  return gateOn ? "launch" : "gated"
}

export function launchDescriptor(entry) {
  const e = entry || {}
  if (e.kind === "task") {
    return {
      kind: "job",
      jobSeed: {
        name: e.title || "",
        icon: "sparkle",
        action: e.prompt || "",
        kind: "chat",
        rawTrigger: null,
        triggerLabel: null,
        scope: [],
        paramInputs: [],
        modelId: null,
        effort: null,
      },
    }
  }
  const seed = e.job_seed || {}
  return {
    kind: "job",
    jobSeed: {
      name: seed.name || e.title,
      icon: seed.icon || "sparkle",
      action: seed.action || "",
      kind: seed.kind,
      rawTrigger: seed.trigger_spec,
      triggerLabel: seed.trigger_label,
      scope: (seed.scope || []).slice(),
      paramInputs: (seed.param_inputs || []).slice(),
      modelId: seed.model_id || null,
      effort: seed.effort || null,
    },
  }
}
